import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ROUTES } from './routes';
import { ProtectedRoute, PublicRoute } from './ProtectedRoute';
import { LoadingScreen } from '@/components/common/LoadingScreen';
import { MainLayout } from '@/components/layout/MainLayout';

// Redirect invited team members from /reset-password?email=xxx to /set-password?email=xxx
const ResetPasswordGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const email = queryParams.get('email');

  if (email) {
    return <Navigate to={`${ROUTES.setPassword}?email=${encodeURIComponent(email)}`} replace />;
  }

  return <>{children}</>;
};

// For /set-password: render ResetPasswordPage for invite flow, SetPasswordPage for registration
const SetPasswordRouteGuard: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const email = queryParams.get('email');

  // Invite flow: reuse existing ResetPasswordPage (it already handles ?email= query param)
  if (email) {
    return <ResetPasswordPage />;
  }

  // Registration flow: use SetPasswordPage
  return <SetPasswordPage />;
};

// Lazy load pages for code splitting
// Auth pages
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'));
const RegisterPage = lazy(() => import('@/features/auth/pages/RegisterPage'));
const VerificationCodePage = lazy(() => import('@/features/auth/pages/VerificationCodePage'));
const ResetPasswordPage = lazy(() => import('@/features/auth/pages/ResetPasswordPage'));
const SetPasswordPage = lazy(() => import('@/features/auth/pages/SetPasswordPage'));

// Onboarding pages
const CollegeDetailsVerificationPage = lazy(
  () => import('@/features/auth/pages/CollegeDetailsVerificationPage')
);
const CollegeVerificationPage = lazy(() => import('@/features/auth/pages/CollegeVerificationPage'));
const CollegeDreamCompaniesPage = lazy(
  () => import('@/features/auth/pages/CollegeDreamCompaniesPage')
);

// Main app pages
const DashboardPage = lazy(() => import('@/features/dashboard/pages/DashboardPage'));
const ManageCandidatesPage = lazy(
  () => import('@/features/manage-candidates/pages/ManageCandidatesPage')
);
const ManageJobsPage = lazy(() => import('@/features/manage-jobs/pages/ManageJobsPage'));
const ManageApplicationsPage = lazy(
  () => import('@/features/manage-applications/pages/ManageApplicationsPage')
);
const CourseManagementPage = lazy(() => import('@/features/courses/pages/CourseManagementPage'));
const AnnouncementsPage = lazy(() => import('@/features/announcements/pages/AnnouncementsPage'));
const SettingsPage = lazy(() => import('@/features/settings/pages/SettingsPage'));
const BatchManagementPage = lazy(() => import('@/features/batches/pages/BatchManagementPage'));
const BatchDetailsPage = lazy(() => import('@/features/batches/pages/BatchDetailsPage'));
const CreateBatchPage = lazy(() => import('@/features/batches/pages/CreateBatchPage'));
const LearningModulePage = lazy(() => import('@/features/learning/pages/LearningModulePage'));
const EventCalendarPage = lazy(() => import('@/features/calendar/pages/EventCalendarPage'));
const JobPreviewPage = lazy(() => import('@/features/manage-jobs/pages/JobPreviewPage'));
const NotificationsPage = lazy(() => import('@/features/notifications/pages/NotificationsPage'));

// Error pages
const NotFoundPage = lazy(() => import('@/components/common/NotFoundPage'));

export const AppRoutes: React.FC = () => {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Public routes */}
        <Route
          path={ROUTES.signIn}
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path={ROUTES.register}
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />
        <Route
          path={ROUTES.verificationCode}
          element={
            <PublicRoute>
              <VerificationCodePage />
            </PublicRoute>
          }
        />
        <Route
          path={ROUTES.resetPassword}
          element={
            <PublicRoute>
              <ResetPasswordGuard>
                <ResetPasswordPage />
              </ResetPasswordGuard>
            </PublicRoute>
          }
        />
        <Route
          path={ROUTES.resetPasswordPath}
          element={
            <PublicRoute>
              <ResetPasswordPage />
            </PublicRoute>
          }
        />
        <Route
          path={ROUTES.setPassword}
          element={
            <PublicRoute>
              <SetPasswordRouteGuard />
            </PublicRoute>
          }
        />

        {/* Onboarding routes (auth required but no main layout) */}
        <Route
          path={ROUTES.collegeDetailsVerification}
          element={
            <ProtectedRoute>
              <CollegeDetailsVerificationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.collegeVerification}
          element={
            <ProtectedRoute>
              <CollegeVerificationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.collegeCompaniesDetails}
          element={
            <ProtectedRoute>
              <CollegeDreamCompaniesPage />
            </ProtectedRoute>
          }
        />

        {/* Protected routes with main layout - using nested routes for sidebar persistence */}
        <Route
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route path={ROUTES.dashboard} element={<DashboardPage />} />
          <Route path={ROUTES.manageCandidates} element={<ManageCandidatesPage />} />
          <Route path={ROUTES.manageJobs} element={<ManageJobsPage />} />
          <Route path={ROUTES.manageApplications} element={<ManageApplicationsPage />} />
          <Route path={ROUTES.manageCourse} element={<CourseManagementPage />} />
          <Route path={ROUTES.announcements} element={<AnnouncementsPage />} />
          <Route path={ROUTES.settings} element={<SettingsPage />} />
          <Route path={ROUTES.batchManagement} element={<BatchManagementPage />} />
          <Route path={ROUTES.batchDetails} element={<BatchDetailsPage />} />
          <Route path={ROUTES.createBatch} element={<CreateBatchPage />} />
          <Route path={ROUTES.learningModule} element={<LearningModulePage />} />
          <Route path={ROUTES.eventCalendar} element={<EventCalendarPage />} />
          <Route path={ROUTES.jobPreview} element={<JobPreviewPage />} />
          <Route path={ROUTES.notifications} element={<NotificationsPage />} />
        </Route>

        {/* Redirect root to dashboard or sign-in */}
        <Route path="/" element={<Navigate to={ROUTES.signIn} replace />} />

        {/* 404 Not Found */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
};
